import { Duration, formatDistanceStrict, formatDuration, Locale } from "date-fns";
import { Duration as ProtoDuration } from "google-protobuf/google/protobuf/duration_pb";
import { Timestamp } from "google-protobuf/google/protobuf/timestamp_pb";
import xbytes from "xbytes";
import { appI18n, getI18nLocale } from "../../modules/i18n/i18n";
import { getAppLocaleConfig } from "../../modules/i18n/localeRegistry";

const getLocaleConfig = () => getAppLocaleConfig(getI18nLocale());
type FormatterTranslationKey =
    | "common:format.yes"
    | "common:format.no"
    | "common:format.unavailable"
    | "common:format.duration.year"
    | "common:format.duration.month"
    | "common:format.duration.week"
    | "common:format.duration.day"
    | "common:format.duration.hour"
    | "common:format.duration.minute"
    | "common:format.duration.second"
    | "common:format.durationCompact.day"
    | "common:format.durationCompact.hour"
    | "common:format.durationCompact.minute"
    | "common:format.durationCompact.second"
    | "common:format.pluralLabel"
    | "common:format.pluralLabelEndingInS";
const translateCommon = (key: FormatterTranslationKey, options?: Record<string, string | number>) => appI18n.t(key, options);

export const formatBool = (b: boolean) => translateCommon(b ? "common:format.yes" : "common:format.no");

function numberWithDecimal(num = 0, decimalPlace = 0) {
    if (typeof decimalPlace === "undefined" || decimalPlace === null) {
        return num;
    } else {
        let _num = num.toFixed(decimalPlace);
        return parseFloat(_num);
    }
}

function numberWithDecimalS(num = 0, decimalPlace = 0) {
    const n = numberWithDecimal(num, decimalPlace);
    return n ? n.toString() : n;
}

export function formatMicroseconds(us: number) {
    let microSecond = us;
    const microSecondUnit = "μs";
    if (typeof microSecond === "undefined") {
        return 0;
    }
    if (microSecond < 1000) {
        if (microSecond !== 0) {
            return numberWithDecimalS(microSecond, null) + " μs";
        } else {
            return "0 μs";
        }
    }
    let millisecond = microSecond / 1000.0;
    if (millisecond < 1000) {
        return numberWithDecimalS(millisecond, 3) + " ms";
    }
    let second = millisecond / 1000.0;
    return numberWithDecimalS(second, 3) + " s";
}

export enum KnownDataType {
    GENERIC,
    NUMBER,
    BOOL,
    CAPACITY,
    THROUGHPUT,
    DATE,
    DATE_RELATIVE,
    PERCENT,
    DURATION,
    SIMPLE_STRING,
    ENUM,
    DURATION_MILLISECONDS,
    DURATION_MICROSECONDS,
    CURRENCY_USD,
}

export const convertTimestampObjectToDate = (timestamp: Timestamp.AsObject) => {
    const t = new Timestamp().setSeconds(timestamp.seconds).setNanos(timestamp.nanos);
    return t.toDate();
};

export const convertDateObjectToTimestamp = (date: Date): Timestamp => {
    return new Timestamp().setSeconds(Math.floor(date.getTime() / 1000));
};

export const formatKnownDataType = (v: any, type: KnownDataType) => {
    if (v === undefined) {
        return translateCommon("common:format.unavailable");
    }
    if (type === KnownDataType.BOOL) {
        return formatBool(v);
    }
    if (type === KnownDataType.CAPACITY) {
        return xbytes(v, { iec: true });
    }
    if (type === KnownDataType.THROUGHPUT) {
        return `${xbytes(v, { iec: true })}/s`;
    }
    if (type === KnownDataType.DATE) {
        let date = v;
        if (!!v.seconds) {
            date = convertTimestampObjectToDate(v);
        }
        return new Intl.DateTimeFormat(getLocaleConfig().intlLocale, {
            dateStyle: "medium",
            timeStyle: "medium",
        }).format(date);
    }
    if (type === KnownDataType.DATE_RELATIVE) {
        let date = v;
        if (!!v.seconds) {
            date = convertTimestampObjectToDate(v);
        }
        return formatDistanceStrict(date, new Date(), {
            addSuffix: true,
            locale: getLocaleConfig().dateFnsLocale,
        });
    }
    if (type === KnownDataType.PERCENT) {
        const num = parseFloat(`${v || 0}`);
        if (num === 0) {
            return new Intl.NumberFormat(getLocaleConfig().intlLocale, {
                style: "percent",
                maximumFractionDigits: 2,
            }).format(0);
        }
        return new Intl.NumberFormat(getLocaleConfig().intlLocale, {
            style: "percent",
            maximumFractionDigits: 2,
        }).format(num / 100);
    }
    if (type === KnownDataType.DURATION_MILLISECONDS) {
        const seconds = v.seconds ?? v.getSeconds();
        const nanos = v.nanos ?? v.getNanos();
        return `${(seconds * 1000 + nanos / 1000 / 1000).toFixed(2)}ms`;
    }
    if (type === KnownDataType.CURRENCY_USD) {
        const USDollar = Intl.NumberFormat(getLocaleConfig().intlLocale, { style: "currency", currency: "USD" });
        return USDollar.format(v);
    }
    if (type === KnownDataType.NUMBER) {
        const num = parseFloat(`${v || 0}`);
        return num.toLocaleString(getLocaleConfig().intlLocale);
    }
    return v;
};

export const formatProtoDuration = (v: ProtoDuration): string => {
    return formatDuration(convertProtoDurationToDuration(v), { locale: getLocaleConfig().dateFnsLocale });
};

export const formatProtoDurationObject = (v: ProtoDuration.AsObject) => {
    if (v === undefined) {
        return translateCommon("common:format.unavailable");
    }
    const durationSecs = v.seconds;
    const durationNanos = v.nanos;
    const fullDurationInSeconds = durationSecs + durationNanos / 1000 / 1000 / 1000;
    if (fullDurationInSeconds < 1) {
        return `${fullDurationInSeconds.toFixed(2)}s`;
    }
    const dateObj = new Date(fullDurationInSeconds * 1000);
    let hours: number | string = dateObj.getUTCHours();
    let minutes: number | string = dateObj.getUTCMinutes();
    let seconds: number | string = dateObj.getSeconds();

    if (hours < 10) {
        hours = `0${hours}`;
    }
    if (minutes < 10) {
        minutes = `0${minutes}`;
    }
    if (seconds < 10) {
        seconds = `0${seconds}`;
    }
    return `${hours}:${minutes}:${seconds}`;
};
export const formatDurationFromSeconds = (
    v: number,
    options?: {
        format?: string[];
        zero?: boolean;
        delimiter?: string;
        locale?: Locale;
        abbreviated?: boolean;
    }
): string => {
    const min = 60;
    const hr = min * 60;
    const day = hr * 24;
    const week = day * 7;
    const month = day * 30;
    const year = day * 365;

    const years = Math.floor(v / year);
    const months = Math.floor((v % year) / month);
    const weeks = Math.floor(((v % year) % month) / week);
    const days = Math.floor(v / day);
    const hours = Math.floor((v % day) / hr);
    const minutes = Math.floor(((v % day) % hr) / min);
    const seconds = ((v % day) % hr) % min;
    const duration = {
        years,
        months,
        weeks,
        days,
        hours,
        minutes,
        seconds,
    };
    const locale = options?.locale ?? getLocaleConfig().dateFnsLocale;

    if (options?.format) {
        // Only consume units that the caller asked for, so ["days", "hours"]
        // still shows 400 days, while ["years", "days"] shows 1 year 35 days.
        const unitSizes: Array<[keyof Duration, number]> = [
            ["years", year],
            ["months", month],
            ["weeks", week],
            ["days", day],
            ["hours", hr],
            ["minutes", min],
            ["seconds", 1],
        ];
        let remaining = Number.isFinite(v) && v > 0 ? v : 0;
        const requested: Duration = {};
        for (const [unit, size] of unitSizes) {
            if (!options.format.includes(unit)) {
                continue;
            }
            if (unit === "seconds") {
                requested[unit] = Math.round(remaining);
                remaining = 0;
                continue;
            }
            const count = Math.floor(remaining / size);
            requested[unit] = count;
            remaining -= count * size;
        }
        return formatDuration(requested, { ...options, locale });
    }

    if (options?.abbreviated) {
        const parts: string[] = [];

        if (years > 0) {
            parts.push(`${years}${translateCommon("common:format.duration.year")}`);
        }
        if (months > 0) {
            parts.push(`${months}${translateCommon("common:format.duration.month")}`);
        }
        if (weeks > 0) {
            parts.push(`${weeks}${translateCommon("common:format.duration.week")}`);
        }
        if (days > 0 && !years && !months) {
            parts.push(`${days}${translateCommon("common:format.duration.day")}`);
        }
        if (hours > 0 && !years && !months && !weeks) {
            parts.push(`${hours}${translateCommon("common:format.duration.hour")}`);
        }
        if (minutes > 0 && !years && !months && !weeks && !days) {
            parts.push(`${minutes}${translateCommon("common:format.duration.minute")}`);
        }
        if ((seconds > 0 || parts.length === 0) && !years && !months && !weeks && !days) {
            parts.push(`${Math.round(seconds)}${translateCommon("common:format.duration.second")}`);
        }

        return parts.join(" ");
    }

    if (!!years) {
        return formatDuration(duration, { format: ["years"], locale });
    }

    if (!!months) {
        return formatDuration(duration, { format: ["months"], locale });
    }

    if (!!weeks) {
        return formatDuration(duration, { format: ["weeks"], locale });
    }

    if (!!days) {
        return formatDuration(duration, { format: ["days"], locale });
    }
    return formatDuration(duration, { format: ["hours", "minutes", "seconds"], locale });
};

/** Compact detailed duration for dense stats UIs, e.g. "3D 22H 24M 23S". */
export const formatDurationCompactDetailed = (totalSeconds?: number | null): string => {
    if (totalSeconds == null || !Number.isFinite(totalSeconds) || totalSeconds < 0) {
        return translateCommon("common:format.unavailable");
    }
    const rounded = Math.round(totalSeconds);
    const day = 24 * 60 * 60;
    const hour = 60 * 60;
    const minute = 60;

    const days = Math.floor(rounded / day);
    const hours = Math.floor((rounded % day) / hour);
    const minutes = Math.floor((rounded % hour) / minute);
    const seconds = rounded % minute;

    const parts: string[] = [];
    if (days > 0) {
        parts.push(`${days}${translateCommon("common:format.durationCompact.day")}`);
    }
    if (days > 0 || hours > 0) {
        parts.push(`${hours}${translateCommon("common:format.durationCompact.hour")}`);
    }
    if (days > 0 || hours > 0 || minutes > 0) {
        parts.push(`${minutes}${translateCommon("common:format.durationCompact.minute")}`);
    }
    parts.push(`${seconds}${translateCommon("common:format.durationCompact.second")}`);
    return parts.join(" ");
};

const convertProtoDurationToDuration = (v: ProtoDuration): Duration => {
    const duration = {
        seconds: v.getSeconds(),
    };

    return duration;
};

export const formatTitleCase = (string: string): string => {
    const array = string.split(" ");
    const newArray = array.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
    return newArray.join(" ");
};

export const convertNumToProtoDuration = (value: number, unit: "s" | "ms") => {
    if (unit === "s") {
        const seconds = Math.floor(value);
        const nanos = Math.floor((value - seconds) * 1000 * 1000 * 1000);
        return new ProtoDuration().setSeconds(seconds).setNanos(nanos);
    } else {
        const seconds = Math.floor(value / 1000);
        const nanos = Math.floor((value - seconds * 1000) * 1000 * 1000);
        return new ProtoDuration().setSeconds(seconds).setNanos(nanos);
    }
};

export const formatSnakeCaseToPascalCase = (s: string, withSpace: boolean = false) => {
    // convert snake_case to  PascalCase or spaced title case
    // if each chunk is less than 3 characters, it would be all upper case

    if (s.length === 0) {
        return "";
    }

    const chunks = s.split("_");
    const result = chunks.map((chunk) => {
        if (chunk.length <= 2) {
            return chunk.toUpperCase();
        }
        return formatTitleCase(chunk);
    });
    if (withSpace) {
        return result.join(" ").trim();
    }
    return result.join("").trim();
};

export const formatPlural = (label: string, count: number) => {
    if (!getI18nLocale().startsWith("en")) {
        return label;
    }
    return translateCommon("common:format.pluralLabel", { count, label });
};

export const formatCapacityParts = (bytes: number): { value: string; unit: string } => {
    const formatted = formatKnownDataType(bytes, KnownDataType.CAPACITY);
    const separator = formatted.lastIndexOf(" ");
    return separator === -1 ? { value: formatted, unit: "" } : { value: formatted.slice(0, separator), unit: formatted.slice(separator + 1) };
};

export type SizeUnit = "bytes" | "KiB" | "MiB" | "GiB";

export const convertBytesToUnit = (bytes: number, unit: SizeUnit): number => {
    switch (unit) {
        case "bytes":
            return bytes;
        case "KiB":
            return bytes / 1024;
        case "MiB":
            return bytes / (1024 * 1024);
        case "GiB":
            return bytes / (1024 * 1024 * 1024);
    }
};

export const convertUnitToBytes = (value: number, unit: SizeUnit): number => {
    switch (unit) {
        case "bytes":
            return value;
        case "KiB":
            return value * 1024;
        case "MiB":
            return value * 1024 * 1024;
        case "GiB":
            return value * 1024 * 1024 * 1024;
    }
};

export const getBestSizeUnit = (bytes: number): SizeUnit => {
    if (bytes >= 1024 * 1024 * 1024) return "GiB";
    if (bytes >= 1024 * 1024) return "MiB";
    if (bytes >= 1024) return "KiB";
    return "bytes";
};
